// One contractor weekly report (V0.4.15): what SWI AI read, the compare with the week before, the cross-check with
// PM C3 and SWI's response (points raised with PBS + the client summary). SWI staff only.
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { api, write } from '../lib/api';
import { fmtDate, fmtDateTime } from '../lib/format';
import { compareWeeks, crossCheck, norm, WEEK_STATUS, type Change, type Finding, type PmItem, type WkBuilding } from '../lib/weekly';
import { Button, Empty, errText, useToast } from '../ui/kit';
import { C3Busy, C3Loader, C3PageLoading } from '../ui/C3Loader';
import { SendWindow } from '../ui/claim/SendWindow';
import { useViewer } from '../ui/Viewer';
import { period, weekName, type WeeklyRow } from './Weekly';

type Tab = 'read' | 'compare' | 'check' | 'response';
const KIND: Record<string, { label: string; tone: string; help: string }> = {
  moved: { label: 'Moved on', tone: 'ok', help: 'Changed since last week' },
  new: { label: 'New', tone: 'info', help: 'First time in a report' },
  repeated: { label: 'Repeated', tone: 'warn', help: 'Same words as last week' },
  dropped: { label: 'Dropped', tone: 'bad', help: 'Gone without being closed' },
};
const FIND: Record<string, { label: string; tone: string }> = {
  not_in_pm: { label: 'Not in PM C3', tone: 'bad' },
  contradicts: { label: 'Report contradicts itself', tone: 'warn' },
  ours: { label: 'Ours to act on', tone: 'info' },
  matched: { label: 'Matched', tone: 'ok' },
};
const LA: Record<string, [string, string]> = { in_progress: ['In progress', 'info'], complete: ['Complete', 'ok'], starting: ['Starting', 'info'], planned: ['Planned', 'mute'], on_hold: ['On hold', 'bad'] };
const plusDays = (n: number) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
const regPath = (t: string) => (t === 'rfi' ? 'rfis' : t === 'claim' ? 'claims' : 'variations');

export default function WeeklyReport() {
  const { id } = useParams();
  const { siteId, can } = useApp();
  const toast = useToast();
  const viewer = useViewer();
  const [sp, setSp] = useSearchParams();
  const tab = (['read', 'compare', 'check', 'response'].includes(sp.get('tab') ?? '') ? sp.get('tab') : 'read') as Tab;
  const setTab = (t: Tab) => setSp(t === 'read' ? {} : { tab: t }, { replace: true });
  const editable = can('weekly_reports', 4);

  const all = useRows<WeeklyRow>(
    () => supabase.from('weekly_reports').select('*').eq('site_id', siteId ?? '00000000-0000-0000-0000-000000000000').is('deleted_at', null),
    [siteId],
    ['weekly_reports'],
  );
  const pm = useRows<PmItem>(
    () => supabase.from('items').select('id, type, number, ref_no, title, status, building_ids, stage, swi_approved_at, client_approved_at').eq('site_id', siteId ?? '00000000-0000-0000-0000-000000000000').in('type', ['variation', 'rfi', 'claim']).is('deleted_at', null),
    [siteId],
  );
  const blds = useRows<{ id: string; name: string; short_code: string | null }>(
    () => supabase.from('buildings').select('id, name, short_code').eq('site_id', siteId ?? '00000000-0000-0000-0000-000000000000').is('deleted_at', null),
    [siteId],
  );
  const rep = all.rows.find((r) => r.id === id) ?? null;
  const earlier = useMemo(() => {
    if (!rep) return [];
    const key = rep.period_to ?? rep.created_at.slice(0, 10);
    return all.rows.filter((r) => r.id !== rep.id && r.read && (r.period_to ?? r.created_at.slice(0, 10)) < key).sort((a, b) => (b.period_to ?? '').localeCompare(a.period_to ?? ''));
  }, [all.rows, rep]);
  const prev = earlier[0] ?? null;
  const changes: Change[] = useMemo(() => (rep?.read ? compareWeeks(rep.read, prev?.read ?? null, earlier.slice(1, 6).map((r) => r.read!)) : []), [rep, prev, earlier]);
  const findings: Finding[] = useMemo(() => (rep?.read ? crossCheck(rep.read, pm.rows, blds.rows) : []), [rep, pm.rows, blds.rows]);
  const open = findings.filter((f) => f.kind !== 'matched');

  if (all.loading) return <C3PageLoading label="Loading the weekly report" />;
  if (!rep) return <Empty>Weekly report not found. <Link to="/weekly">Back to the list</Link></Empty>;

  const decide = async (key: string, v: string | null, summary: string) => {
    const d = { ...(rep.decisions ?? {}) };
    if (v) d[key] = v;
    else delete d[key];
    try {
      await write('weekly_reports', 'update', { id: rep.id, values: { decisions: d, status: rep.status === 'read' ? 'in_review' : rep.status }, summary });
      await all.reload();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  };
  const openPdf = () => rep.file_id && viewer({ id: rep.file_id, name: `${rep.title ?? weekName(rep)}.pdf`, mime: 'application/pdf' });

  return (
    <div className="wk-page">
      <div className="small"><Link to="/weekly">Contractor weekly reports</Link> › {weekName(rep)}</div>
      <div className="wk-head">
        <div>
          <h1>{weekName(rep)} · {period(rep)}</h1>
          <p className="muted">
            {rep.read?.contractor ?? 'Contractor'} weekly construction update{rep.report_date ? `, dated ${fmtDate(rep.report_date)}` : ''}
            {rep.title ? ` · ${rep.title}` : ''}
          </p>
        </div>
        <div className="wk-row">
          <span className={`wk-badge ${WEEK_STATUS[rep.status]?.tone ?? 'mute'}`}>{WEEK_STATUS[rep.status]?.label ?? rep.status}</span>
          {rep.file_id ? <Button onClick={openPdf}>Open PDF</Button> : null}
          {editable && rep.file_id && (rep.status === 'failed' || rep.read) ? (
            <Button
              onClick={() =>
                void api('weekly-read', { report_id: rep.id, file_id: rep.file_id })
                  .then(() => toast({ tone: 'ok', text: 'SWI AI is reading it again' }))
                  .catch((e) => toast({ tone: 'error', text: errText(e) }))
              }
            >
              Read again
            </Button>
          ) : null}
          {editable && rep.read && rep.status !== 'reviewed' ? (
            <Button kind="primary" onClick={() => void write('weekly_reports', 'update', { id: rep.id, values: { status: 'reviewed' }, summary: `${weekName(rep)} marked reviewed` }).then(() => all.reload()).catch((e) => toast({ tone: 'error', text: errText(e) }))}>
              Mark reviewed
            </Button>
          ) : null}
        </div>
      </div>

      {rep.status === 'reading' ? (
        <div className="wk-reading"><C3Loader size={56} variant="levels" label="SWI AI is reading" /><div><b>SWI AI is reading the report…</b><div className="muted small">This takes a minute or two for a 30-page PDF. The page fills in on its own.</div></div></div>
      ) : null}
      {rep.status === 'failed' ? <div className="error-box">SWI AI could not read it: {rep.error ?? 'unknown error'}. Try "Read again".</div> : null}

      {rep.read ? (
        <>
          <div className="wk-tabs" role="tablist" aria-label={weekName(rep)}>
            {([
              ['read', 'SWI AI read'],
              ['compare', `Compare with ${prev ? weekName(prev).toLowerCase() : 'last week'} · ${changes.length}`],
              ['check', `Cross-check PM C3 · ${open.filter((f) => !rep.decisions?.[f.key]).length}`],
              ['response', 'SWI response'],
            ] as const).map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
                {l}
              </button>
            ))}
          </div>
          {tab === 'read' ? <ReadTab b={rep.read.buildings} notes={rep.read.notes} onPdf={openPdf} /> : null}
          {tab === 'compare' ? <CompareTab changes={changes} prevName={prev ? weekName(prev) : null} /> : null}
          {tab === 'check' ? <CheckTab rep={rep} findings={findings} editable={editable} decide={decide} blds={blds.rows} reload={() => void pm.reload()} /> : null}
          {tab === 'response' ? <ResponseTab rep={rep} changes={changes} findings={open} editable={editable} reload={() => void all.reload()} /> : null}
        </>
      ) : null}
    </div>
  );
}

function ReadTab({ b, notes, onPdf }: { b: WkBuilding[]; notes: string | null; onPdf: () => void }) {
  const [sel, setSel] = useState(b[0]?.name ?? '');
  const cur = b.find((x) => x.name === sel) ?? b[0];
  if (!cur) return <Empty>SWI AI found no building sections in this report.</Empty>;
  const pg = (p: number | string | null | undefined) =>
    p ? (
      <button type="button" className="wk-src" onClick={onPdf} title="Open the PDF">
        p. {p}
      </button>
    ) : null;
  return (
    <div className="wk-card">
      <div className="wk-row wk-pad" role="group" aria-label="Building">
        {b.map((x) => (
          <button key={x.name} type="button" className={`chip ${x.name === cur.name ? 'on' : ''}`} onClick={() => setSel(x.name)}>
            {x.name}
          </button>
        ))}
      </div>
      {notes ? <div className="wk-sec"><div className="banner banner-info">SWI AI: {notes}</div></div> : null}
      <section className="wk-sec">
        <h3>Progress this week {pg(cur.pages)}</h3>
        <p className="wk-text">{cur.progress ?? '—'}</p>
      </section>
      {cur.lookahead.length ? (
        <section className="wk-sec">
          <h3>Look-ahead</h3>
          <div className="wk-tblw">
            <table className="wk-tbl">
              <thead><tr><th>Area</th><th>Activity</th><th>Status in report</th></tr></thead>
              <tbody>
                {cur.lookahead.map((l, i) => (
                  <tr key={i}><td>{l.area ?? '—'}</td><td>{l.text}</td><td>{l.status ? <span className={`wk-badge ${LA[l.status]![1]}`}>{LA[l.status]![0]}</span> : '—'}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      {(
        [
          ['Variations mentioned', cur.variations],
          ['RFIs', cur.rfis],
          ['Open issues', cur.open_issues],
        ] as const
      ).map(([t, list]) =>
        list.length ? (
          <section key={t} className="wk-sec">
            <h3>{t}</h3>
            {list.map((x, i) => (
              <div key={i} className="wk-li">
                <span className="grow">{x.ref ? <b>{x.ref} — </b> : null}{x.text}</span>
                {x.status ? <span className="wk-badge warn">{x.status}</span> : null}
                {pg(x.page)}
              </div>
            ))}
          </section>
        ) : null,
      )}
      {cur.procurement.length ? (
        <section className="wk-sec">
          <h3>Procurement, materials and samples</h3>
          <div className="wk-tblw">
            <table className="wk-tbl">
              <thead><tr><th>Item</th><th>Status in report</th><th>ETA</th><th>Flag</th></tr></thead>
              <tbody>
                {cur.procurement.map((p, i) => (
                  <tr key={i}>
                    <td>{p.item}</td>
                    <td>{p.status}</td>
                    <td>{p.eta ?? '—'}</td>
                    <td>{p.flag === 'critical' ? <span className="wk-badge bad">Critical</span> : p.flag === 'no_eta' ? <span className="wk-badge warn">No ETA</span> : p.flag === 'waiting' ? <span className="wk-badge warn">Waiting on review</span> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      {cur.inspections.length || cur.delays.length || cur.claim ? (
        <section className="wk-sec">
          <h3>Claim, inspections and hold-ups</h3>
          {cur.claim ? <div className="wk-li"><span className="grow"><b>Client claim — </b>{cur.claim}</span></div> : null}
          {cur.inspections.map((x, i) => <div key={`i${i}`} className="wk-li"><span className="grow"><b>Inspection — </b>{x.text}</span>{x.date ? <span className="wk-badge info">{fmtDate(x.date)}</span> : null}</div>)}
          {cur.delays.map((x, i) => <div key={`d${i}`} className="wk-li"><span className="grow"><b>Hold-up — </b>{x}</span></div>)}
        </section>
      ) : null}
    </div>
  );
}

function CompareTab({ changes, prevName }: { changes: Change[]; prevName: string | null }) {
  const [kind, setKind] = useState<string>('');
  if (!prevName) return <Empty>This is the first weekly report SWI AI has read — next week's will be compared with it.</Empty>;
  const shown = kind ? changes.filter((c) => c.kind === kind) : changes;
  const groups = [...new Set(shown.map((c) => c.building))];
  return (
    <>
      <div className="wk-sum" role="group" aria-label="Filter changes">
        {Object.entries(KIND).map(([k, v]) => (
          <button key={k} type="button" className={`wk-sbox ${kind === k ? 'on' : ''}`} aria-pressed={kind === k} onClick={() => setKind(kind === k ? '' : k)}>
            <span className={`wk-badge ${v.tone}`}>{v.label}</span>
            <b>{changes.filter((c) => c.kind === k).length}</b>
            <span className="muted small">{v.help}</span>
          </button>
        ))}
      </div>
      <div className="wk-card">
        {!shown.length ? <Empty>No changes of this kind.</Empty> : null}
        {groups.map((g) => (
          <div key={g}>
            <div className="wk-grp">{g}</div>
            {shown.filter((c) => c.building === g).map((c) => (
              <div key={c.key} className="wk-ch">
                <span>
                  <span className={`wk-badge ${KIND[c.kind]!.tone}`}>{KIND[c.kind]!.label}{c.weeks && c.weeks > 1 ? ` · ${c.weeks} wks` : ''}</span>
                  <div className="muted small wk-mt">{c.section}</div>
                </span>
                <span className="muted"><span className="wk-lbl">{prevName} said</span>{c.was ?? '—'}</span>
                <span><span className="wk-lbl">This week says</span>{c.now ?? <span className="muted">Not mentioned</span>}</span>
                <span className="wk-note">{c.note}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

function CheckTab({ rep, findings, editable, decide, blds, reload }: { rep: WeeklyRow; findings: Finding[]; editable: boolean; decide: (k: string, v: string | null, s: string) => Promise<void>; blds: { id: string; name: string }[]; reload: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState('');
  const [showMatched, setShowMatched] = useState(false);
  const open = findings.filter((f) => f.kind !== 'matched');
  const matched = findings.filter((f) => f.kind === 'matched');
  async function createRequests(f: Finding) {
    setBusy(f.key);
    try {
      const bld = blds.find((b) => norm(b.name) === norm(f.building) || norm(b.name).includes(norm(f.building)));
      const b = rep.read!.buildings.find((x) => x.name === f.building);
      const fromPage = f.says.startsWith('The register') ? [] : (b?.variations ?? []).filter((v) => f.says.includes(v.text));
      const list = fromPage.length ? fromPage.map((v) => ({ title: v.text, ref: v.ref })) : [{ title: f.says.replace(/^.*lists "|" \(.*$/g, '').slice(0, 200), ref: null }];
      const batch = crypto.randomUUID();
      for (const v of list)
        await write('items', 'insert', {
          batch_id: batch,
          values: { site_id: rep.site_id, type: f.area === 'RFIs' ? 'rfi' : 'variation', subtype: f.area === 'RFIs' ? 'RFI' : 'Var', title: v.title, ref_no: v.ref, status: 'open', building_ids: bld ? [bld.id] : [], notes: `From the PBS weekly report ${weekName(rep)} (${period(rep)}) — not yet in PM C3.` },
          summary: `Added from ${weekName(rep)}: ${v.title}`,
        });
      await decide(f.key, 'done', `${weekName(rep)}: added ${list.length} to PM C3`);
      reload();
      toast({ tone: 'ok', text: `${list.length} added to ${f.area === 'RFIs' ? 'RFIs' : 'Variations & Requests'}` });
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy('');
    }
  }
  return (
    <>
      <div className="wk-row">
        {Object.entries(FIND).map(([k, v]) => (
          <span key={k} className={`wk-badge ${v.tone}`}>{v.label} · {findings.filter((f) => f.kind === k).length}</span>
        ))}
      </div>
      <div className="wk-card">
        {!open.length ? <Empty>Everything in the report matches PM C3.</Empty> : null}
        {open.map((f) => {
          const d = rep.decisions?.[f.key];
          return (
            <div key={f.key} className={`wk-f ${d ? 'done' : ''}`}>
              <span>
                <b>{f.building}</b>
                <div><span className={`wk-badge ${FIND[f.kind]!.tone}`}>{FIND[f.kind]!.label}</span></div>
                <div className="muted small wk-mt">{f.area}</div>
              </span>
              <span>{f.says}{f.page ? <span className="wk-src static">p. {f.page}</span> : null}</span>
              <span className="muted">
                {f.pm}
                {f.itemIds.length ? (
                  <div className="wk-mt">
                    {f.itemIds.map((iid) => (
                      <Link key={iid} to={`/r/${regPath(pmType(f))}/${iid}`} className="small">Open in PM C3</Link>
                    ))}
                  </div>
                ) : null}
              </span>
              <span className="wk-acts">
                {d ? (
                  <>
                    <span className={`wk-badge ${d === 'done' ? 'ok' : d === 'raise' ? 'info' : 'mute'}`}>{d === 'done' ? 'Done' : d === 'raise' ? 'To raise with PBS' : 'Not needed'}</span>
                    {editable ? <button type="button" className="linkbtn small" onClick={() => void decide(f.key, null, `${weekName(rep)}: reopened a check`)}>Undo</button> : null}
                  </>
                ) : editable ? (
                  <>
                    {f.action === 'create_request' ? (
                      <Button kind="primary" disabled={Boolean(busy)} onClick={() => void createRequests(f)}>
                        {busy === f.key ? <C3Busy text="Adding…" accent /> : f.area === 'RFIs' ? 'Add to RFIs' : 'Add to Variations'}
                      </Button>
                    ) : null}
                    {f.action === 'raise_pbs' || f.action === 'follow_up' || f.action === 'create_request' ? (
                      <Button kind={f.action === 'create_request' ? 'secondary' : 'primary'} onClick={() => void decide(f.key, 'raise', `${weekName(rep)}: to raise with PBS`)}>Raise with PBS</Button>
                    ) : null}
                    {f.action === 'open_item' && f.itemIds[0] ? <Link className="btn btn-primary" to={`/r/${regPath(pmType(f))}/${f.itemIds[0]}`}>Open in PM C3</Link> : null}
                    <Button onClick={() => void decide(f.key, 'not_needed', `${weekName(rep)}: check marked not needed`)}>Not needed</Button>
                  </>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>
      <div className="wk-card">
        <div className="wk-row wk-pad" style={{ justifyContent: 'space-between' }}>
          <b>Matched — no action ({matched.length})</b>
          <button type="button" className="linkbtn small" onClick={() => setShowMatched((s) => !s)}>{showMatched ? 'Hide' : 'Show all'}</button>
        </div>
        {showMatched ? matched.map((f) => <div key={f.key} className="wk-m"><span className="wk-badge ok">Matched</span><span className="grow">{f.building} · {f.says}</span><span className="muted small">{f.pm}</span></div>) : null}
      </div>
    </>
  );
}
const pmType = (f: Finding) => (f.area === 'RFIs' ? 'rfi' : f.area === 'Claims' ? 'claim' : 'variation');

type Ask = { key: string; text: string; due: string; on: boolean };
function ResponseTab({ rep, changes, findings, editable, reload }: { rep: WeeklyRow; changes: Change[]; findings: Finding[]; editable: boolean; reload: () => void }) {
  const toast = useToast();
  const sent = new Set((rep.pbs_asks ?? []).map((a) => a.text));
  const suggested = useMemo<Ask[]>(() => {
    const out: Ask[] = [];
    for (const f of findings) {
      const d = rep.decisions?.[f.key];
      if (d === 'not_needed' || d === 'done') continue;
      if (f.kind === 'contradicts' || d === 'raise' || f.action === 'follow_up')
        out.push({ key: f.key, text: `${f.building} — ${f.says} Please confirm the status.`, due: plusDays(5), on: f.kind === 'contradicts' || d === 'raise' });
    }
    for (const c of changes) {
      if (c.kind === 'repeated' && c.section === 'Progress update') out.push({ key: c.key, text: `${c.building} — no progress reported${c.weeks && c.weeks > 2 ? ` for ${c.weeks} weeks` : ''}. Send the current status and next steps.`, due: plusDays(3), on: true });
      if (c.kind === 'dropped') out.push({ key: c.key, text: `${c.building} — "${c.was}" is no longer in the report. Was it resolved?`, due: plusDays(5), on: true });
      if (c.kind === 'new' && /reference number/.test(c.note)) out.push({ key: c.key, text: `${c.building} — "${c.now}": give the RFI / reference number.`, due: plusDays(5), on: false });
    }
    return out.filter((a) => !sent.has(a.text));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findings, changes, rep.decisions, rep.pbs_asks]);
  const [asks, setAsks] = useState<Ask[]>(suggested);
  useEffect(() => setAsks(suggested), [suggested]);
  const [own, setOwn] = useState('');
  const [note, setNote] = useState(rep.internal_note ?? '');
  const [summary, setSummary] = useState(rep.client_summary ?? '');
  useEffect(() => setSummary(rep.client_summary ?? ''), [rep.client_summary]);
  const [busy, setBusy] = useState('');
  const [send, setSend] = useState<'pbs' | 'client' | null>(null);
  const [attach, setAttach] = useState(true);
  const ticked = asks.filter((a) => a.on);

  async function save(values: Record<string, unknown>, what: string) {
    setBusy(what);
    try {
      await write('weekly_reports', 'update', { id: rep.id, values: { ...values, status: rep.status === 'read' ? 'in_review' : rep.status }, summary: `${weekName(rep)}: ${what}` });
      reload();
      toast({ tone: 'ok', text: 'Saved' });
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy('');
    }
  }
  async function draft() {
    setBusy('draft');
    try {
      const r = await api<{ text: string }>('weekly-action', { action: 'summary', report_id: rep.id });
      setSummary(r.text);
      reload();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy('');
    }
  }
  return (
    <div className="wk-cols">
      <div className="wk-stack">
        <div className="wk-card">
          <div className="wk-pad"><h2>Raise with PBS</h2><p className="muted small">From the compare and cross-check tabs. Ticked points go to PBS in one email with a reply-by date.</p></div>
          {!asks.length ? <p className="muted small wk-pad">Nothing to raise this week.</p> : null}
          {asks.map((a, i) => (
            <div key={a.key} className="wk-ask">
              <input type="checkbox" checked={a.on} disabled={!editable} aria-label={`Raise: ${a.text}`} onChange={(e) => setAsks(asks.map((x, k) => (k === i ? { ...x, on: e.target.checked } : x)))} />
              <span className="grow">{a.text}</span>
              <input type="date" className="wk-due" value={a.due} disabled={!editable} aria-label="Reply by" onChange={(e) => setAsks(asks.map((x, k) => (k === i ? { ...x, due: e.target.value } : x)))} />
            </div>
          ))}
          {editable ? (
            <div className="wk-ask">
              <input
                className="wk-own"
                value={own}
                placeholder="Add your own point…"
                aria-label="Add your own point"
                onChange={(e) => setOwn(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && own.trim()) {
                    setAsks([...asks, { key: `own-${Date.now()}`, text: own.trim(), due: plusDays(5), on: true }]);
                    setOwn('');
                  }
                }}
              />
            </div>
          ) : null}
          {(rep.pbs_asks ?? []).length ? (
            <div className="wk-pad muted small">
              Already raised: {(rep.pbs_asks ?? []).length} point{(rep.pbs_asks ?? []).length === 1 ? '' : 's'} · last {fmtDateTime((rep.pbs_asks ?? []).at(-1)?.sent_at ?? null)}
            </div>
          ) : null}
          {editable ? (
            <div className="wk-row wk-pad wk-end">
              <Button kind="primary" disabled={!ticked.length} onClick={() => setSend('pbs')}>
                Email {ticked.length} point{ticked.length === 1 ? '' : 's'} to PBS…
              </Button>
            </div>
          ) : null}
        </div>
        <div className="wk-card wk-pad wk-stack">
          <h2>SWI notes on this week</h2>
          <label className="field">
            <span className="field-label">Internal note (SWI only)</span>
            <textarea rows={4} value={note} disabled={!editable} onChange={(e) => setNote(e.target.value)} aria-label="Internal note" />
          </label>
          <div className="wk-row wk-end">
            <span className="wk-badge mute">SWI only — never in the client summary or emails to PBS</span>
            {editable ? <Button disabled={busy === 'note' || note === (rep.internal_note ?? '')} onClick={() => void save({ internal_note: note.trim() || null }, 'internal note saved')}>Save note</Button> : null}
          </div>
        </div>
      </div>

      <div className="wk-card">
        <div className="wk-row wk-pad" style={{ justifyContent: 'space-between' }}>
          <div><h2>Client summary</h2><p className="muted small">Drafted by SWI AI from the report and SWI's checks — edit freely.</p></div>
          {rep.client_sent_at ? <span className="wk-badge ok">Sent {fmtDate(rep.client_sent_at)}</span> : <span className="wk-badge ai">Draft</span>}
        </div>
        <div className="wk-pad wk-stack">
          <textarea className="wk-summary" rows={14} value={summary} disabled={!editable} aria-label="Client summary" placeholder="Press “Draft with SWI AI”, or write the summary here." onChange={(e) => setSummary(e.target.value)} />
          <label className="check small"><input type="checkbox" checked={attach} onChange={(e) => setAttach(e.target.checked)} /> Attach the PBS report (PDF)</label>
        </div>
        {editable ? (
          <div className="wk-row wk-pad wk-end">
            <Button disabled={Boolean(busy)} onClick={() => void draft()}>{busy === 'draft' ? <C3Busy text="SWI AI is writing…" /> : summary ? 'Redraft with SWI AI' : 'Draft with SWI AI'}</Button>
            <Button disabled={Boolean(busy) || summary === (rep.client_summary ?? '')} onClick={() => void save({ client_summary: summary.trim() || null }, 'client summary saved')}>Save</Button>
            <Button kind="primary" disabled={!summary.trim() || summary !== (rep.client_summary ?? '')} onClick={() => setSend('client')}>Review and send…</Button>
          </div>
        ) : null}
      </div>

      {send === 'pbs' ? (
        <SendWindow
          title={`Raise with PBS — ${weekName(rep)}`}
          endpoint="weekly-action"
          body={{ action: 'send_pbs', report_id: rep.id, asks: ticked.map((a) => ({ text: a.text, due: a.due })) }}
          onClose={() => setSend(null)}
          onSent={() => {
            setSend(null);
            reload();
          }}
        />
      ) : null}
      {send === 'client' ? (
        <SendWindow
          title={`Client summary — ${weekName(rep)}`}
          endpoint="weekly-action"
          body={{ action: 'send_client', report_id: rep.id, attach }}
          onClose={() => setSend(null)}
          onSent={() => {
            setSend(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}
